import { CoreApiVersionPath, RosaCoreApiService } from '@/rosa';
import type { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import type { BuildingUserAccess } from '../../interfaces/Access.interface';
import type { PasswordUpdate, ProfileRole } from '../../interfaces/Profile.interface';
import type { UserCore, UserId } from '../../interfaces/User.interface';
import { BrandingProvider } from '../branding/branding.provider';

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreProfileProvider {
    private _rosaCoreApiService = inject(RosaCoreApiService);
    brandingProvider = inject(BrandingProvider);

    updateProfileField(userId: UserId, fieldName: string, newValue: any): Observable<void> {
        return this._rosaCoreApiService.patch(`${CoreApiVersionPath.V3}users/${userId}/${fieldName}`, { newValue });
    }

    updatePicture(userUid: UserId, file: FormData): Observable<string> {
        return this._rosaCoreApiService.post(`${CoreApiVersionPath.V3}users/${userUid}/picture`, file);
    }

    updateRight(objs: BuildingUserAccess[], branding: string): Observable<HttpResponse<any>> {
        return this._rosaCoreApiService.post(
            `${CoreApiVersionPath.V3}buildings/accesses`,
            { branding, accesses: objs },
            {
                'content-type': 'application/json'
            }
        );
    }

    deleteUserAccesses(accesses: BuildingUserAccess[]): Observable<HttpResponse<any>> {
        return this._rosaCoreApiService.post(`${CoreApiVersionPath.V3}buildings/accesses/delete`, accesses);
    }

    updatePasswordUsingToken(userEmail: string, token: string, newPassword: string): Observable<any[]> {
        return this._rosaCoreApiService.patch(
            `accounts/confirmResetPassword?email=${userEmail}&resetToken=${token}`,
            { newPassword },
            null,
            true
        );
    }

    updatePassword(userEmail: string, passwords: PasswordUpdate) {
        return this._rosaCoreApiService.patch(`${CoreApiVersionPath.V3}accounts/${userEmail}/password`, passwords);
    }

    addNewUser(user: any) {
        return this._rosaCoreApiService.post(`register`, user, null, true);
    }

    acceptInvitation(accountId: string, token: string): Observable<any> {
        return this._rosaCoreApiService.post(`${CoreApiVersionPath.V3}accounts/${accountId}/invitations/accept`, {
            invitationToken: token
        });
    }

    fetchRights(): Observable<string[]> {
        return this._rosaCoreApiService.get<string[]>(`${CoreApiVersionPath.V3}roles/rights`);
    }

    fetchCurrentUser(userId: UserId): Observable<UserCore> {
        return this._rosaCoreApiService.get(`${CoreApiVersionPath.V3}users/${userId}`);
    }

    getUserProfileRoles$(): Observable<ProfileRole[]> {
        return this._rosaCoreApiService.get(`${CoreApiVersionPath.V3}users/profiles/roles/me`);
    }
}
